import { useEffect, useState } from "react";
import api, { openSignedDownload } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertCircle,
  Download,
  Globe,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Stat } from "@/components/admin/helpers";
import { AnalyticsCard } from "@/components/admin/AnalyticsCard";
import { FunnelCard } from "@/components/admin/FunnelCard";
import { LeaderboardCard } from "@/components/admin/LeaderboardCard";
import { MembershipsCard } from "@/components/admin/MembershipsCard";
import { ClaimsTable } from "@/components/admin/ClaimsTable";

const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney",
];

function readSortPref() {
  try {
    return localStorage.getItem("cs_admin_sort_activity") === "1";
  } catch {
    return false;
  }
}

function applyClaimFilter(claims, filter, statusFilter) {
  return claims.filter((c) => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (
      filter &&
      ![c.full_name, c.address, c.carrier, c.damage_type, c.zip_code]
        .join(" ")
        .toLowerCase()
        .includes(filter.toLowerCase())
    ) {
      return false;
    }
    return true;
  });
}

export default function AdminDashboard() {
  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [membershipSummary, setMembershipSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [lastRuns, setLastRuns] = useState({});
  const [savingTz, setSavingTz] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortByActivity, setSortByActivity] = useState(readSortPref);
  const [runningDigest, setRunningDigest] = useState(false);
  const [runningScan, setRunningScan] = useState(false);

  // Persist sort preference per device
  useEffect(() => {
    try {
      localStorage.setItem(
        "cs_admin_sort_activity",
        sortByActivity ? "1" : "0",
      );
    } catch (err) {
      // localStorage can be disabled / quota-exceeded — sort preference just
      // resets next session. Log so ops sees if private-mode users hit this.
      logger.debug("[admin] sort-pref persist failed:", err);
    }
  }, [sortByActivity]);

  const refreshLastRuns = async () => {
    try {
      const r = await api.get("/admin/last-runs");
      setLastRuns(r.data || {});
    } catch (err) {
      // Last-run timestamps are advisory metadata. Log for ops; UI hides
      // the section when state is empty so user impact is zero.
      logger.debug("[admin] last-runs fetch failed:", err?.response?.status);
    }
  };

  const saveTimezone = async (tz) => {
    setSavingTz(true);
    try {
      const r = await api.put("/admin/settings", { timezone: tz });
      setSettings(r.data);
      toast.success(`Schedules now run in ${tz}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update timezone");
    } finally {
      setSavingTz(false);
    }
  };

  const runShareScan = async () => {
    if (runningScan) return;
    setRunningScan(true);
    try {
      const r = await api.post(`/admin/shares/auto-revoke`);
      const { revoked, scanned } = r.data || {};
      toast.success(`Scan complete: ${revoked} revoked, ${scanned} scanned`);
      const f = await api.get("/admin/analytics/funnel");
      setFunnel(f.data);
      refreshLastRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setRunningScan(false);
    }
  };

  const viewDigestErrors = async (period) => {
    try {
      const r = await api.get(`/admin/digests/failures?period=${period}`);
      const failures = r.data?.failures || [];
      if (failures.length === 0) {
        toast.success("No failures recorded in this run");
        return;
      }
      toast.error(
        `${failures.length} ${period} digest failure${failures.length === 1 ? "" : "s"}`,
        {
          description: failures
            .slice(0, 3)
            .map(
              (f) =>
                `${f.to}: ${(f.error || "").slice(0, 80) || "unknown error"}`,
            )
            .join("\n"),
          duration: 12000,
        },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load failures");
    }
  };

  const runDigest = async (period) => {
    if (runningDigest) return;
    setRunningDigest(true);
    try {
      const r = await api.post(`/admin/digests/run?period=${period}`);
      const { sent, skipped } = r.data || {};
      toast.success(
        `${period} digest dispatched: ${sent} sent, ${skipped} skipped`,
      );
      refreshLastRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest run failed");
    } finally {
      setRunningDigest(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/claims"),
      api.get("/admin/stats"),
      api.get("/admin/analytics"),
      api.get("/admin/analytics/funnel"),
      api.get("/admin/settings"),
      api.get("/admin/last-runs"),
      api.get("/admin/memberships"),
      api.get("/admin/analytics/funnel/recipients"),
    ]).then(([c, s, a, f, st, lr, mm, rc]) => {
      setClaims(c.data);
      setStats(s.data);
      setAnalytics(a.data);
      setFunnel(f.data);
      setSettings(st.data);
      setLastRuns(lr.data || {});
      const mmData = mm.data || {};
      setMemberships(
        Array.isArray(mmData) ? mmData : mmData.memberships || [],
      );
      setMembershipSummary(
        Array.isArray(mmData) ? null : mmData.summary || null,
      );
      setRecipients(rc.data || null);
    });
  }, []);

  const filtered = applyClaimFilter(claims, filter, statusFilter);
  const sortedFiltered = sortByActivity
    ? [...filtered].sort((a, b) =>
        (a.last_activity_at || "").localeCompare(b.last_activity_at || ""),
      )
    : filtered;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <Header
        runDigest={runDigest}
        runningDigest={runningDigest}
        lastRuns={lastRuns}
        viewDigestErrors={viewDigestErrors}
      />

      {stats && (
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Stat label="Total claims" value={stats.total_claims} />
          <Stat label="In review" value={stats.in_review} accent />
          <Stat label="Approved" value={stats.approved} />
          <Stat label="Contractors" value={stats.contractors} />
        </div>
      )}

      <AnalyticsCard analytics={analytics} />

      {settings && (
        <SettingsCard
          settings={settings}
          savingTz={savingTz}
          saveTimezone={saveTimezone}
        />
      )}

      <FunnelCard
        funnel={funnel}
        runShareScan={runShareScan}
        runningScan={runningScan}
        lastRuns={lastRuns}
      />

      <LeaderboardCard recipients={recipients} />

      <MembershipsCard
        memberships={memberships}
        summary={membershipSummary}
      />

      <ClaimsTable
        filtered={filtered}
        sortedFiltered={sortedFiltered}
        filter={filter}
        setFilter={setFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        sortByActivity={sortByActivity}
        setSortByActivity={setSortByActivity}
      />
    </div>
  );
}

function Header({ runDigest, runningDigest, lastRuns, viewDigestErrors }) {
  return (
    <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div>
        <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-2">
          Admin · Case evaluation
        </p>
        <h1 className="font-['Outfit'] text-4xl font-semibold tracking-tight">
          Claims CRM
        </h1>
      </div>
      <div className="flex flex-col gap-2 md:items-end">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-full border-[#1E3F33] text-[#1E3F33] hover:bg-[#1E3F33] hover:text-[#F9F8F6]"
            onClick={() => runDigest("daily")}
            disabled={runningDigest}
            data-testid="run-daily-digest-button"
          >
            <Mail className="w-4 h-4 mr-2" />
            Run daily digest
          </Button>
          <Button
            className="btn-primary rounded-full"
            onClick={() => runDigest("weekly")}
            disabled={runningDigest}
            data-testid="run-weekly-digest-button"
          >
            <Mail className="w-4 h-4 mr-2" />
            {runningDigest ? "Sending…" : "Run weekly digest"}
          </Button>
        </div>
        {(lastRuns.digest_daily || lastRuns.digest_weekly) && (
          <DigestRunsLine
            lastRuns={lastRuns}
            viewDigestErrors={viewDigestErrors}
          />
        )}
      </div>
    </div>
  );
}

function DigestRunsLine({ lastRuns, viewDigestErrors }) {
  return (
    <p
      className="text-xs text-gray-500 flex items-center flex-wrap gap-x-2 gap-y-1"
      data-testid="last-digest-runs"
    >
      {lastRuns.digest_weekly && (
        <>
          <span>
            Weekly last ran{" "}
            {new Date(lastRuns.digest_weekly.created_at).toLocaleString()}
          </span>
          {lastRuns.digest_weekly.result?.failed > 0 && (
            <button
              onClick={() => viewDigestErrors("weekly")}
              className="inline-flex items-center gap-1 text-[#C25934] hover:underline"
              data-testid="view-weekly-digest-errors"
            >
              <AlertCircle className="w-3 h-3" />
              {lastRuns.digest_weekly.result.failed} error
              {lastRuns.digest_weekly.result.failed === 1 ? "" : "s"}
            </button>
          )}
          <span>·</span>
        </>
      )}
      {lastRuns.digest_daily ? (
        <>
          <span>
            Daily last ran{" "}
            {new Date(lastRuns.digest_daily.created_at).toLocaleString()}
          </span>
          {lastRuns.digest_daily.result?.failed > 0 && (
            <button
              onClick={() => viewDigestErrors("daily")}
              className="inline-flex items-center gap-1 text-[#C25934] hover:underline"
              data-testid="view-daily-digest-errors"
            >
              <AlertCircle className="w-3 h-3" />
              {lastRuns.digest_daily.result.failed} error
              {lastRuns.digest_daily.result.failed === 1 ? "" : "s"}
            </button>
          )}
        </>
      ) : (
        <span>Daily not yet run</span>
      )}
    </p>
  );
}

function SettingsCard({ settings, savingTz, saveTimezone }) {
  return (
    <Card className="border-[#E5E7EB] mb-6" data-testid="admin-settings-card">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Globe className="w-4 h-4 text-[#C25934]" />
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">
                Schedule timezone
              </p>
              <p className="text-sm text-gray-700">
                Weekly digest (Mon 14:00) + daily share scan (03:00) run in
                this zone.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={settings.timezone}
              onValueChange={saveTimezone}
              disabled={savingTz}
            >
              <SelectTrigger className="w-56" data-testid="timezone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() =>
                openSignedDownload("admin_audit_csv", "", "/admin/audit.csv")
              }
              className="inline-flex items-center gap-2 text-sm font-medium rounded-full border border-[#1E3F33] text-[#1E3F33] bg-white hover:bg-[#1E3F33] hover:text-[#F9F8F6] px-4 py-2 transition-colors"
              data-testid="audit-csv-download"
            >
              <Download className="w-4 h-4" />
              Export audit CSV
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
